import { useCallback, useMemo } from 'react';

import { QueryErrorState } from '@/lib/components';
import type { CrmObject } from '@/types/crm';

import { useAssociations } from '../../hooks/useAssociations';
import type { AssociationChange } from '../buildProjectedObject';
import { useRecordDraft } from '../contexts/RecordDraftContext';
import { CollapsibleSection } from './collapsible-section';
import { ContactCard } from './contact-card';

const EMPTY_ASSOCIATION_CHANGES: Record<string, AssociationChange> = {};

interface PeopleSectionProps {
  object: CrmObject;
  sectionTitle: string;
}

export function PeopleSection({ object, sectionTitle }: PeopleSectionProps) {
  const orgId = object.org_id;
  const associationChanges =
    useRecordDraft()?.associationChanges ?? EMPTY_ASSOCIATION_CHANGES;

  const contactAssociations = useMemo(
    () =>
      (object.associations ?? []).filter(
        assoc =>
          assoc.association_type_name.includes('_to_contact') &&
          !(
            assoc.association_type_name.includes('_owner') &&
            assoc.association_type_name.includes('_to_owner')
          )
      ),
    [object.associations]
  );

  const { associations, isLoading, error, hasPartialError, refetch } =
    useAssociations(orgId, contactAssociations, {
      sourceEntityTypeName: object.entity_type_name,
    });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  if (contactAssociations.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <CollapsibleSection title={sectionTitle} defaultExpanded>
        <div className='space-y-3'>
          {[1, 2].map(i => (
            <div
              key={i}
              className='h-16 bg-[var(--color-bg-tertiary)]/10 rounded-xl animate-pulse'
            />
          ))}
        </div>
      </CollapsibleSection>
    );
  }

  if (error) {
    return (
      <CollapsibleSection title={sectionTitle} defaultExpanded>
        <QueryErrorState
          error={error}
          label="We couldn't load people"
          onRetry={handleRetry}
          size='sm'
        />
      </CollapsibleSection>
    );
  }

  const contacts = associations
    .map(assoc => assoc.object)
    .filter((obj): obj is CrmObject => obj !== null);

  if (contacts.length === 0) {
    return (
      <CollapsibleSection title={sectionTitle} defaultExpanded>
        {hasPartialError ? (
          <div className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
            <p className='text-xs md:text-sm font-medium text-utility-error-800'>
              Some contacts couldn&apos;t be loaded
            </p>
            <button
              type='button'
              onClick={handleRetry}
              className='mt-1 text-xs md:text-sm text-utility-error-800 underline hover:no-underline'
            >
              Retry
            </button>
          </div>
        ) : (
          <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-2'>
            No contacts
          </p>
        )}
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title={sectionTitle} defaultExpanded>
      <div className='space-y-3'>
        {contacts.map(contact => (
          // A proposal that adds or removes someone from this company marks
          // the card, so the change reads the same from the company as it does
          // from the contact's own "Works At" row. The card owns the
          // treatment — wrapping it in a second outlined element rendered a
          // double border with mismatched corner radii.
          <ContactCard
            key={contact.entity_id}
            contact={contact}
            changeMarker={associationChanges[contact.entity_id]}
          />
        ))}
        {hasPartialError && (
          <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-1'>
            Some contacts couldn&apos;t be loaded.{' '}
            <button
              type='button'
              onClick={handleRetry}
              className='text-xs md:text-sm text-[var(--color-text-tertiary)] underline hover:no-underline cursor-pointer bg-transparent border-none p-0'
            >
              Retry
            </button>
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
}
